import { EFinancingCompany, EFinancingProgram } from '@if/core/types'
import { can } from '@if/mui'
import { merchantSupportsInviteMessages, programSupportsDocumentUploadRequest } from '@src/data/merchant-config-selector'
import type { Merchant } from '@src/types/Merchant'

/**
 * Access rules shared by every gate (route `<Guard>`, nav links, in-page checks)
 * so they can't drift. `can<{ merchant }>()` entries fold runtime merchant data
 * into the rule itself; consumers just resolve and pass `{ merchant }`.
 */
export const access = {
  createApplication: can({ permission: 'canCreateApplications' }),
  applicationDetail: can({ permission: 'hasDetailAccess' }),
  manageUsers: can({ permission: 'isAdmin' }),
  // iFinance-only: the report timers in CreditApplication filter on IFinance, so Iceberg settings would never send.
  reportSettings: can<{ merchant: Merchant }>({
    permission: 'canSetReportProperties',
    flag: 'useMerchantReportSettingsMenu',
    rule: ({ merchant }) => merchant.financingCompanyId === EFinancingCompany.IFinance,
  }),
  loanCalculator: can<{ merchant: Merchant }>({
    rule: ({ merchant }) =>
      merchant.supportedFinancingPrograms.includes(EFinancingProgram.Products) ||
      merchant.supportedFinancingPrograms.includes(EFinancingProgram.Personal),
  }),
  customBuydownBuilder: can<{ merchant: Merchant }>({
    permission: 'canManagePromotions',
    flag: 'useCustomFlashPlan',
    rule: ({ merchant }) => merchant.flashPlanDefinedInManageMerchant,
  }),
  merchantStatistics: can<{ merchant: Merchant }>({
    permission: 'canViewStatistics',
    flag: 'useMerchantStatistics',
    rule: ({ merchant }) => merchant.financingCompanyId === EFinancingCompany.IFinance,
  }),
  protectionPlan: can<{ merchant: Merchant }>({
    rule: ({ merchant }) => merchant.financingCompanyId === EFinancingCompany.IFinance,
  }),
  inviteApplicant: can<{ merchant: Merchant }>({
    permission: 'canSendWelcomeMessage',
    rule: ({ merchant }) =>
      merchantSupportsInviteMessages(merchant.supportedFinancingPrograms, merchant.financingCompanyId),
  }),
  invitationHistory: can({ permission: 'canSendWelcomeMessage', flag: 'enableInvitationHistoryMerchantDB' }),
  // Banner users invite on behalf of a subsidiary: gate on the rollout switch and on whether any
  // active subsidiary supports the invite program for the banner's company — never the banner's own
  // programs, so the button stays visible when only the children support invites. Inactive
  // subsidiaries don't count: they're filtered out of the picker, so the button must match.
  bannerInvitations: can<{ subsidiaries: Merchant[]; company: EFinancingCompany }>({
    permission: 'canSendWelcomeMessage',
    flag: 'enableBannerInvitations',
    rule: ({ subsidiaries, company }) =>
      subsidiaries.some((m) => m.isActive && merchantSupportsInviteMessages(m.supportedFinancingPrograms, company)),
  }),
  messaging: can({ flag: 'useInstantMessage' }),
  unreadMessagePopupOptOut: can({ flag: 'enableDeviceSettingNewMessagePopUpOptOutMerchDB' }),
  ifxFinancingProgram: can({ flag: 'useIFXFinancingProgram' }),
  customBuydownActive: can<{ merchant: Merchant | null }>({
    flag: 'useCustomFlashPlan',
    rule: ({ merchant }) => merchant?.flashPlanDefinedInManageMerchant ?? false,
  }),
  productChargebackConfig: can({ flag: 'useProductChargebackOrRetentionConfig' }),
  showRate: can({ flag: 'useMerchDBshowRate' }),
  contractUpdateOnExpiry: can({ flag: 'useMerchDBContractUpdateOnExpiry' }),
  requestDocuments: can<{ financingProgramId: EFinancingProgram }>({
    flag: 'useRequestDocumentsMerchantDB',
    rule: ({ financingProgramId }) => programSupportsDocumentUploadRequest(financingProgramId),
  }),
  termAndPlanSelection: can({ flag: 'useCustomFlashPlan' }),
  identityVerification: can({ flag: 'enableVouchedForThinCreditB2B' }),
  symTechPrime: can({ flag: 'useProductsSymTechPrime' }),
}
